// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the wire under the sync
//  ---------------------------------------------------------------------------
//  Until this file existed, a second device only learned about the first WHEN
//  IT ASKED — on a local edit, on focus, on coming back online, or when
//  somebody tapped. Two of your own devices sitting awake side by side never
//  converged, because neither of them was being asked anything.
//
//  This is the room they are both in. It carries the day's documents between
//  them at socket speed and, for everything else, it carries the FACT that
//  something changed so the other device pulls instead of waiting to be looked
//  at. Both are the same three lines from here: subscribe, publish, close.
//
//  THE DURABLE PATH IS UNTOUCHED. /api/sync is still the source of truth, still
//  a compare-and-swap, still the thing that reconciles a phone that spent an
//  hour in a tunnel. The room holds sockets in memory and never replays: a
//  device that was asleep missed the frame and will never see it, and that is
//  correct, because the frame was never the record. This layer makes the app
//  faster when it works and EXACTLY AS CAPABLE AS BEFORE when it does not.
//
//  So the failure mode is silence. No transport, no route, no binding, no
//  WebSocket, a factory that throws, a frame that is a lie, a socket that
//  drops — every one of them resolves to today's behaviour, with nothing shown
//  to the user and nothing thrown at the caller.
//
//  THE LIVE PATH IS THE PULL PATH, MINUS THE HTTP. A `doc` frame carries
//  {key, value} in the SYNC DOCUMENT namespace — exactly the shape GET
//  /api/sync already returns — and the receiving side runs it through
//  mdSanitiseDoc and mdApplyDoc, the same untrusted-input validator and the
//  same appliers a pull uses, unchanged. That one choice buys three
//  correctness properties without writing a line for any of them:
//
//    the seed-vs-follow rule is already honoured, so a device cannot seed a
//    rival Yjs history for records the other device authored — the failure
//    where Yjs keeps one history whole and discards the other, permanently
//
//    a live apply is already not undoable, because mdApplyDoc wraps every
//    application in mdHistoryQuiet
//
//    a hostile frame is already handled, because a pulled document has always
//    been treated as untrusted input
//
//  WHAT IT COSTS, PLAINLY: a live apply writes no md:syncMeta, so the document
//  is re-offered on this device's next push and two devices push identical
//  bytes instead of one. That is already what happens after every pull. The
//  alternative — bookkeeping on apply — would record as synced a document the
//  server may never have seen, which is precisely the loss md-sync.js paid for
//  once already. One redundant push is the cheaper side of the trade.
//
//  IT IS THE TRANSPORT SEAM'S FIRST REAL CONSUMER, and that is worth more than
//  the speed. md-transport.ts promises that nothing above it learns which
//  transport it is talking to; until something above it talked at all, that
//  promise could not be checked. Nothing here mentions a WebSocket, an origin,
//  a URL or a status code — tests/live.test.js reads this file and says so —
//  and since 1.2 it does not name the transport either: which one is
//  mdTransportName()'s answer, in md-transport.ts, so the day
//  md-transport-nostr.ts is written the edit is there and nowhere else.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  /* Which transport is md-transport.ts's question to answer, not this file's.
     It held the name itself once — a DEFAULT rather than a dependency, which
     read as harmless and was one of two places the seam's central promise was
     kept. 1.2 moved it to mdTransportName(). */

  /* THE CLIENT CANNOT NAME ITS OWN ROOM. `self` is a request, not a name — the
     Worker works out `acct:<user.id>` from the session cookie and mints nothing
     a client could spell (server/room.js). Being signed in to ManDana is not
     the same as being signed in as you, and a user id is not a secret. */
  const ROOM = 'self';
  let activeDesk: string | null = null;

  /* { transport, sub } while this device is in the room, and null otherwise.
     One room, one socket, however many surfaces are mounted. */
  let session: any = null;
  let pending: Promise<any> | null = null;

  /* Bumped by every stop. A start that was still in flight when one happened
     must not install the transport it was waiting for — that would be a socket
     nothing holds a reference to and nothing can close, for as long as the tab
     is open. Sign-out during boot is not a hypothetical: the gate settles
     against the server at about the same moment this opens. */
  let generation = 0;

  const listeners = new Set<(frame: any) => void>();

  function receive(bytes: Uint8Array): Promise<any> | undefined {
    /* Let go means let go. Unsubscribing hands the room back, but a frame
       already in flight still holds a reference to this function, and a
       transport that forgot to release it would go on delivering into an app
       that has signed out. The check is one comparison and it is the last word
       on whether this device is listening. */
    if (!session) return undefined;

    const frame = g.mdLiveDecode(bytes);
    if (!frame) return undefined;     // refused at the door — md-live-frame.ts

    /* The room does not echo (server/room.js), so this is belt and braces. It
       is also what makes a second tab on the same device harmless, and under a
       CRDT an echo is a second operation in the log rather than a repaint. */
    if (frame.dev === g.mdDeviceId()) return undefined;

    for (const fn of [...listeners]) {
      try { fn(frame); } catch { /* one bad listener must not deafen the rest */ }
    }

    peers = mdLivePeersStep(peers, frame, Date.now());
    if (frame.t === 'bye') delete peerDesks[frame.dev];
    else if ((frame.t === 'hello' || frame.t === 'beat') && 'desk' in frame) {
      peerDesks[frame.dev] = frame.desk;
    }
    announce();

    /* AN INCUMBENT ANSWERS A HELLO WITH A BEAT, NEVER WITH A HELLO. A hello
       answered by a hello is two devices greeting each other for as long as
       they are both awake. A beat says the same thing — I am here — and asks
       for nothing back, so the exchange ends after one frame. */
    if (frame.t === 'hello') { mdLivePublish({ t: 'beat' }); return undefined; }
    if (frame.t === 'beat' || frame.t === 'bye') return undefined;

    if (frame.t === 'news') {
      /* Nothing to apply — the frame carries no content. Just stop waiting to
         be looked at: mdSyncSoon is the app's existing debounced pull, asked
         for now instead of on the next focus. It is debounced, so twenty
         nudges arriving together are still one round trip. */
      if (typeof g.mdSyncSoon === 'function') g.mdSyncSoon(0);
      return undefined;
    }

    if (frame.t === 'evict') {
      /* THE ACCOUNT THIS DEVICE IS HOLDING WAS JUST DELETED — from a device
         that is not this one, or the own-device check above would already
         have dropped the frame. That device tears itself down on its own; this
         frame is what makes every OTHER device signed in to the account find
         out in the same second, awake, instead of on its next cold boot when
         it would otherwise ask the durable path and learn the same thing far
         later.

         THE TEARDOWN ITSELF IS NOT WRITTEN HERE. There are four ways to
         learn that an account is gone — this frame, the boot's gate settle, a
         sync that gets the same answer from the server, and the button in
         Settings — and four teardowns that were nearly the same would be four
         that drifted apart, with the drifted one being the one nobody was
         watching. mdGateEvicted (app/md-gate.ts) is the one of them: stop this
         socket, destroy the room and its databases, forget both marks, reload,
         in that order and for the reasons stated there. Returned rather than
         fired, so a caller can await the whole thing exactly as the `doc`
         branch below returns its chain. */
      return typeof g.mdGateEvicted === 'function' ? g.mdGateEvicted() : undefined;
    }

    if (frame.t === 'signout') {
      /* THE SESSIONS ON THIS ACCOUNT WERE JUST ENDED — from a device that is not
         this one, or the own-device check above would have dropped the frame.
         Somebody pressed "Sign out everywhere else" in Settings, and this device
         is one of the "else".

         WHY A FRAME AT ALL, when the database already says so: a socket is
         authenticated once, at the upgrade, and is never asked again. So an
         awake device's socket outlives the session it was opened with and goes
         on relaying documents through the room until its next sync gets a 401
         and tears it down. This is what makes the lock take effect in the same
         second instead of on the next sync — the same reason `evict` exists.

         WHAT IT DOES NOT DO is close a HOSTILE socket. A frame is a courtesy,
         and a client that ignores it keeps relaying until it drops; only the
         room dropping the socket itself would answer that.

         AND IT IS A SIGN-OUT, NOT A DEMOLITION. mdGateSignedOut, deliberately,
         not the eviction above: the account is fine and only this device's key
         to it is gone, so the records, the room and the account marker all stay.
         Handing off to the app's one named sign-out rather than repeating it
         here is what keeps the ways of learning this from drifting apart. */
      return typeof g.mdGateSignedOut === 'function' ? g.mdGateSignedOut() : undefined;
    }

    if (frame.t !== 'doc') return undefined;
    queued += 1;
    applying = applying.then(() => absorb(frame));
    /* Returned so a caller can hold it. The transport ignores it, as a handler
       should; a test that owns the handler awaits it instead of guessing. */
    return applying;
  }

  async function begin(mine: number, options: any) {
    if (typeof g.mdTransportOpen !== 'function') return { ok: false, reason: 'no_seam' };

    /* Tests only. The suite drives this layer against a fake transport
       registered under another name, which is the only way "nothing above the
       seam knows which transport it is" can be CHECKED rather than asserted. */
    const name = typeof options.transport === 'string' && options.transport
      ? options.transport
      : g.mdTransportName();

    const opened = await g.mdTransportOpen(name);
    if (!opened || !opened.ok) {
      return { ok: false, reason: 'no_transport', detail: opened && opened.error };
    }
    const transport = opened.transport;
    const letGo = () => { try { transport.close(); } catch { /* already gone */ } };

    if (mine !== generation) { letGo(); return { ok: false, reason: 'stopped' }; }

    /* The transport asks the room before it opens a socket, so this answer is
       the whole truth: no route, no binding, no session, no network all arrive
       here as `ok: false` and a sentence. The contract says a transport never
       throws; the try costs two lines and means this one never has to. */
    let sub: any = null;
    try {
      sub = await transport.subscribe(ROOM, receive);
    } catch (err: any) {
      letGo();
      return { ok: false, reason: 'no_room', detail: (err && err.message) || String(err) };
    }
    if (!sub || !sub.ok) {
      letGo();
      return { ok: false, reason: 'no_room', detail: sub && sub.error };
    }
    if (mine !== generation) {
      try { sub.unsubscribe(); } catch { /* already gone */ }
      letGo();
      return { ok: false, reason: 'stopped' };
    }

    /* ENROL HERE, AND ONLY HERE. Joining the account's room is the moment this
       device is actually linked — the room is named from the session, so
       reaching it is proof there is an account to be on the roster of. Doing it
       at boot instead would put a row on the list for a browser somebody was
       merely signed out in. It writes once and never again; app/md-devices.ts.

       BEFORE THE WATCHER, deliberately. Left after it, this write would go out
       as news and every device's first launch would nudge the room about
       itself. The durable path carries the roster instead, which is the right
       speed for it: a device appearing in a list is not a keystroke, and the
       peer sees it on the next pull it was going to make anyway. */
    if (typeof g.mdDeviceEnrol === 'function') {
      try {
        if (g.mdDeviceEnrol() && typeof g.mdSyncSoon === 'function') g.mdSyncSoon();
      } catch { /* a roster is not worth a failed start */ }
    }

    /* AND THE SAME FACT, TOLD TO THE SERVER. The roster says this device is on
       the account; this says which session it is holding, which is what lets
       Settings' × end it later (server/auth.js, migration 0009). One moment,
       both halves, because a device on the list that could not be removed would
       be a promise the list cannot keep.

       Not awaited: a stamp is bookkeeping for a later removal, and the room must
       not wait on a round trip to open. Nothing reads the answer — a launch that
       could not reach the server simply stamps on the next one. */
    if (typeof g.mdSessionDevice === 'function') {
      try { void Promise.resolve(g.mdSessionDevice()).catch(() => {}); }
      catch { /* as above: this is never worth a failed start */ }
    }

    /* Watching starts only once this device is actually in a room, and stops
       when it leaves: an app that is not live should not pay for a watcher, and
       md-storage only reads the previous bytes when somebody is listening. */
    const unwatch = typeof g.mdOnStorageWrite === 'function' ? g.mdOnStorageWrite(onWrite) : null;
    /* The window is real time and a tab can go away inside it. */
    if (typeof g.addEventListener === 'function') g.addEventListener('pagehide', leave);

    session = { transport, sub, unwatch };

    /* Announce, then keep announcing. The hello is what lets a device already
       in the room learn about this one without either of them polling; the
       interval is what lets one that never speaks stay visible. */
    peers = {};
    peerDesks = {};
    told = '';
    mdLivePublish({ t: 'hello' });
    beating = g.setInterval(tick, MD_LIVE_BEAT);

    return { ok: true };
  }

  /* ── what leaves, and when ──────────────────────────────────────────────
     Published from the chokepoint's own watcher. md-storage.js is the one door
     every document already passes through, so nothing has to be added to any
     call site and nothing can forget — the same property that makes ⌘Z work
     app-wide, used a second time.

     COALESCED, because one edit is several writes. mdStoreWriteAll writes the
     day and its CRDT sibling in one synchronous run, and a person typing
     produces a write per keystroke. 120 ms is under what reads as instant and
     collapses a typing burst by an order of magnitude. */
  const MD_LIVE_COALESCE = 120;

  /* THE DRAWING SURFACE SETTLES FASTER, and 60 is a measurement rather than a
     preference — tests/strokes-settle.test.js counts frames for the story's own
     burst, ten strokes across three hundred milliseconds.

     What that burst showed. At 120 the frame count was already fine (three
     frames, well inside the budget of six) and the EXPERIENCE was not: the last
     stroke sat in the outbox for 87 ms after the pen stopped, so the second
     person watched a line arrive in blocks. At 60 it is 27 ms and five frames —
     still inside the budget, with a frame to spare. And 30 is ruled out from the
     other side: it is shorter than the 33 ms between strokes, so every window
     closes before the next one lands and the coalescer collapses NOTHING — ten
     strokes, ten frames, worse traffic for no gain.

     `boardfile:` takes the same number because it travels WITH the scene: a
     pasted picture writes the file and the element pointing at it in one
     gesture, and two windows would let the element leave first and render as a
     grey box on the other device until the second window closed.

     Everything else keeps 120. A day, a status and a database row are typed or
     tapped, not drawn, and none of them is watched stroke by stroke. */
  const MD_LIVE_COALESCE_BOARD = 60;

  /** How long this key's family waits before its frame leaves. */
  function mdLiveCoalesceFor(key: unknown): number {
    if (typeof key !== 'string') return MD_LIVE_COALESCE;
    return key.startsWith('board:') || key.startsWith('boardfile:')
      ? MD_LIVE_COALESCE_BOARD : MD_LIVE_COALESCE;
  }

  let outbox: Map<string, any> | null = null;
  /* Tier 2: the FACT that something else changed. One flag, not a list — the
     frame carries no content, so there is nothing to distinguish. */
  let news = false;
  let outTimer: any = null;
  /* When the timer above is set to fire. Held so a later write can compare its
     own deadline against the one already scheduled. */
  let outDue = 0;

  /** Open the window, or pull it forward. THE EARLIEST DEADLINE WINS, and never
      the latest: one timer serves every family, so a stroke landing inside a
      day's open window has to be able to bring the flush forward to its own
      number — otherwise a desk drawn on while somebody types settles at the
      day's speed and the per-family window quietly does nothing. Pushing the
      deadline back would be the mirror mistake: the day already in the outbox
      agreed to leave at its own time, and a later arrival must not make it
      late. */
  function schedule(ms: number): void {
    const now = Date.now();
    if (outTimer != null) {
      if (now + ms >= outDue) return;
      try { g.clearTimeout(outTimer); } catch { /* fine */ }
    }
    outDue = now + ms;
    outTimer = g.setTimeout(mdLiveFlush, ms);
  }

  /* A LIVE APPLY IS NOT A LOCAL EDIT, and the chokepoint cannot tell on its own
     — a document written by an apply looks exactly like one you just typed.
     So it is told, in the same shape md-history uses one layer up for the same
     reason.

     Without this, applying a frame publishes it straight back. That echo does
     terminate — merging is idempotent, so the return trip is an identical
     document and an unchanged write — but it doubles the traffic of every edit
     and it carries nothing the peer does not already have. Convergence never
     depended on it: every local edit is published by its own author, and each
     device merges the other's symmetrically.

     The window is one microtask wide for a day, whose applier is synchronous, so
     no input event can land inside it. A genuinely async applier widens it, and
     the cost of a suppressed write there is one late frame — never a lost byte,
     because the durable path still has it. */
  let quiet = 0;

  function onWrite(change: any): void {
    if (!session || quiet > 0 || !change) return;

    /* Nor while a sync is running. md-sync owns that answer and the loop it
       prevents; see mdSyncBusy. */
    if (typeof g.mdSyncBusy === 'function' && g.mdSyncBusy()) return;

    /* The storage key's name on the wire — `md:events:2026-08-25` travels as
       `events:2026-08-25`. Asked of md-sync rather than worked out here: a
       second grammar for the same keys is the first thing to drift. */
    const key = typeof g.mdSyncDocKeyOf === 'function' ? g.mdSyncDocKeyOf(change.key) : '';
    if (!key) return;                       // a key that never travels at all

    /* The window this write leaves in. Tier 2 has no key on the wire, so it
       waits the ordinary time; tier 1 asks its own family. */
    let window = MD_LIVE_COALESCE;

    if (g.mdLiveCarries(key)) {
      /* TIER 1 — what is on screen, carried whole and merged per field. */
      let value: any = null;
      if (change.after != null) {
        try { value = JSON.parse(change.after); } catch { return; }
      }
      mdLiveOffer(key, value);
      return;
    } else if (typeof g.mdSyncNews === 'function' && g.mdSyncNews(change.key)) {
      /* TIER 2 — a board, a project, a workflow, a database, a setting.
         The frame carries NO CONTENT. The peer answers it by pulling, over the
         durable path, which already knows how to apply every one of these: no
         new applier, no change to the server's key grammar, no new key family.
         Unbounded latency becomes one HTTP round trip for the whole rest of the
         account, for the smaller share of the risk. */
      news = true;
    } else {
      return;                               // held on this device, never offered
    }

    schedule(window);
  }

  /** THIS DEVICE JUST WROTE A TIER 1 DOCUMENT — put it on the wire.

      WHY THIS IS PUBLIC, AND IT IS NOT A CONVENIENCE. A live family needs five
      things, and app/md-families.ts records four of them: a pattern, a
      sanitiser, an applier, a live tier. The fifth is the OFFER — something
      that says "one was just written here" — and for almost every family that
      something is the localStorage chokepoint, watched above.

      DESKS LEFT LOCALSTORAGE IN 1.7 AND 1.8. The scenes went to IndexedDB, the
      watcher stayed on the chokepoint, and from that day a stroke produced no
      frame at all — not a `doc`, and not even the tier 2 `news`, because both
      branches hang off a write this layer never heard about. Every other leg
      was in place: the frame encoded it, the room would have relayed it, the
      server stored it, the sanitiser and md-board.ts's applier were waiting.
      Four legs of five, and the feature was silent. It did not look broken; it
      looked like the desk arriving on the next sync, which is what a desk did
      before there was a wire.

      So md-board.ts announces its own writes here, and the policy — every
      guard, the coalescing window, the outbox — stays in this file where the
      rest of it is. A store that keeps its documents somewhere else is not a
      special case any more; it is a caller.

      Answers whether the document was taken. */
  function mdLiveOffer(key: unknown, value: unknown): boolean {
    /* THE SAME THREE GUARDS THE WATCHER ANSWERS TO, and they are here rather
       than at the caller for the reason above: a second copy of them is a
       second thing to keep in step. Not live, mid-apply (or the frame goes
       straight back where it came from), or mid-sync (md-sync owns that answer
       and the loop it prevents) — in each case there is nothing to send. */
    if (!session || quiet > 0) return false;
    if (typeof g.mdSyncBusy === 'function' && g.mdSyncBusy()) return false;
    if (typeof key !== 'string' || !key || !g.mdLiveCarries(key)) return false;

    if (!outbox) outbox = new Map();
    /* Last write wins within the window, which is right: the document is
       whole, so the newest copy says everything the older ones did. It is also
       what makes a double announcement harmless — md-board.ts still falls back
       to localStorage when IndexedDB is gone, and that write is watched. */
    outbox.set(key, value === undefined ? null : value);
    schedule(mdLiveCoalesceFor(key));
    return true;
  }

  /** Send whatever is waiting, now. Public because the window is real time and
      a tab can go away inside it — the last 120 ms of somebody's typing would
      otherwise never leave the device. */
  function mdLiveFlush(): void {
    if (outTimer != null) { try { g.clearTimeout(outTimer); } catch { /* fine */ } }
    outTimer = null;
    outDue = 0;
    const held = outbox;
    const tell = news;
    outbox = null;
    news = false;
    if (!session) return;
    if (held) for (const [key, value] of held) mdLivePublish({ t: 'doc', key, value });
    /* One nudge for the burst. A drawing hand must not become a pull per
       stroke, and twenty projects reordered in one gesture is still one thing
       for the other device to go and read. */
    if (tell) mdLivePublish({ t: 'news' });
  }

  /* ── what arrives ───────────────────────────────────────────────────────── */
  /* Serial. Applying a document is read-modify-write — two frames for the same
     day landing together would each merge against a state the other has not
     written yet. The chain is also what a caller awaits. */
  let applying: Promise<any> = Promise.resolve();
  let queued = 0;
  let touched = false;
  /* WHICH documents the burst changed, not merely that it changed some. A
     durable pull has named its keys since the day the open desk needed to know
     whether it was one of them; this raised the same event with nothing on it,
     so the canvas listening for `board:<id>` heard a burst that could not
     possibly be about it and re-read nothing. A desk edited on the laptop went
     on showing its old strokes on the iPad, live, with the room connected. */
  let touchedKeys: string[] = [];

  async function absorb(frame: any): Promise<void> {
    quiet += 1;
    try {
      if (typeof g.mdSanitiseDoc !== 'function' || typeof g.mdApplyDoc !== 'function') return;
      /* The same validator a pull uses. `undefined` means unusable — leave the
         local document exactly as it is; `null` is a legitimate deletion. */
      const clean = g.mdSanitiseDoc(frame.key, frame.value);
      if (clean === undefined) return;
      /* And the same applier, which is what makes this the pull path minus the
         HTTP. A family that deliberately declines answers false. */
      /* md-storage's revision moves on an accepted mutation and on nothing
         else, which makes it the honest answer to "does the screen need to
         re-read?". A frame carrying what this device already holds is an
         unchanged write — the chokepoint says so without moving it — and must
         not cost every mdsynced consumer a full re-read of every store. */
      const rev = typeof g.mdStorageRevision === 'function' ? g.mdStorageRevision : null;
      const before = rev ? rev() : 0;
      const applied = await g.mdApplyDoc(frame.key, clean);
      if (applied !== false && (!rev || rev() !== before)) {
        touched = true;
        if (typeof frame.key === 'string') touchedKeys.push(frame.key);
      }
    } catch { /* an applier that threw must not stop the next frame */ } finally {
      quiet -= 1;
      queued -= 1;
      /* ONE event for a burst, dispatched when the last frame has landed. Every
         consumer of `mdsynced` re-reads whole stores behind React's back;
         twenty frames arriving together must not cost twenty of those. */
      if (queued === 0 && touched) {
        /* Taken before the dispatch, and cleared with it: a listener that
           applies something of its own inside the handler must not find its
           key in the NEXT burst's list. */
        const pulledKeys = touchedKeys;
        touched = false;
        touchedKeys = [];
        if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
          g.dispatchEvent(new g.CustomEvent('mdsynced', { detail: { ok: true, live: true, pulledKeys } }));
        }
      }
    }
  }

  /** Join the room this device's account owns. Safe to call from anywhere, as
      often as anything likes: three surfaces mounting is one socket. */
  function mdLiveStart(options?: any): Promise<any> {
    if (session) return Promise.resolve({ ok: true, already: true });
    if (pending) return pending;

    const mine = generation;
    const started = begin(mine, options || {})
      .catch((err: any) => ({ ok: false, reason: 'failed', detail: (err && err.message) || String(err) }))
      .then((res: any) => {
        /* Only if nothing stopped us in the meantime — otherwise `pending` is
           null, or belongs to a start that came after, and clearing it here
           would let a third caller open a second socket. */
        if (mine === generation) pending = null;
        return res;
      });
    pending = started;
    return started;
  }

  /** Leave. Sign-out's job, and the last surface's. */
  /* A closing tab gets one chance to be polite. Nothing depends on it — a peer
     that misses the bye expires two beats later — but the two beats are visible
     on the other device and the frame is free. */
  function leave(): void {
    mdLiveFlush();
    mdLivePublish({ t: 'bye' });
  }

  function mdLiveStop(): void {
    generation += 1;
    pending = null;
    /* Before the session is let go of, because publishing needs it. */
    if (session) mdLivePublish({ t: 'bye' });
    const held = session;
    session = null;
    peers = {};
    told = '';
    if (beating != null) { try { g.clearInterval(beating); } catch { /* fine */ } }
    beating = null;
    /* Dropped rather than sent. Whatever was in the window belongs to a room
       this device has left, and the durable path still holds every byte of it. */
    if (outTimer != null) { try { g.clearTimeout(outTimer); } catch { /* fine */ } }
    outTimer = null;
    outDue = 0;
    outbox = null;
    news = false;
    if (typeof g.removeEventListener === 'function') g.removeEventListener('pagehide', leave);
    if (!held) return;
    if (held.unwatch) { try { held.unwatch(); } catch { /* already gone */ } }
    /* Both, in this order. `unsubscribe` releases the room, which closes its
       socket when it was the last handler; `close` makes that true even if
       something else leaked one. `close` is in the contract precisely because
       an interface that cannot be let go of leaks one socket per navigation. */
    try { held.sub.unsubscribe(); } catch { /* already gone */ }
    try { held.transport.close(); } catch { /* already gone */ }
  }

  function mdLiveIsLive(): boolean {
    return !!session;
  }

  /** Say something into the room. Never throws; answers `ok: false` instead. */
  async function mdLivePublish(frame: any) {
    const held = session;
    if (!held) return { ok: false, reason: 'not_live' };

    /* The version and the device are stamped here rather than by every caller:
       one is a property of the wire and the other is a property of the machine,
       and neither is something a caller of this function gets an opinion about.
       A device that cannot name
       itself cannot publish — md-live-frame refuses a frame with no `dev` —
       and that refusal is right: the room could not tell its frames from a
       peer's, so it would apply its own edits a second time. Such a device can
       still LISTEN, because nothing it hears can be its own. */
    const presenceDesk = frame && (frame.t === 'hello' || frame.t === 'beat')
      ? { desk: activeDesk }
      : {};
    const bytes = g.mdLiveEncode({ ...frame, ...presenceDesk, v: g.MD_LIVE_VERSION, dev: g.mdDeviceId() });
    if (!bytes) return { ok: false, reason: 'bad_frame' };

    try {
      return await held.transport.publish(ROOM, bytes);
    } catch (err: any) {
      return { ok: false, reason: 'publish_failed', detail: (err && err.message) || String(err) };
    }
  }

  /* ── who is here right now ───────────────────────────────────────────────
     A DIFFERENT FACT FROM THE ROSTER, with a different lifetime. The roster
     (app/md-devices.ts) says which devices exist and what they are called, is
     written down, and outlives every socket. Presence says which of them is
     awake, is never written down, and expires on its own — a device is here
     because it just said something, not because anything recorded that it did.

     THE RULES ARE PURE FUNCTIONS OVER A PLAIN TABLE, id -> when it was last
     heard, with the clock passed in. Presence is the easiest thing in this file
     to make flaky, and a test that sleeps for a beat is a test that fails on a
     loaded machine — this one runs twenty sessions. */

  /* How often a live device says it is still here. Long enough that a quiet
     room costs four frames a minute; short enough that a device leaving is gone
     from the list inside a glance. */
  const MD_LIVE_BEAT = 15000;

  /* TWO MISSED BEATS, NOT ONE. A phone changing cell drops a frame without
     leaving the room, and a list that flickers is worse than a list that is one
     beat behind. */
  const MD_LIVE_MISSES = 2;

  /** The table after one frame. A table in, a table out; nothing else moves. */
  function mdLivePeersStep(table: any, frame: any, now: number): Record<string, number> {
    const out: Record<string, number> = { ...(table || {}) };
    const dev = frame && typeof frame.dev === 'string' ? frame.dev : '';
    /* A frame with nobody's name on it never gets this far — md-live-frame
       refuses it — but the rule belongs with the table, not with the door. */
    if (!dev) return out;
    if (frame.t === 'bye') { delete out[dev]; return out; }
    if (!Number.isFinite(now)) return out;
    /* ANY frame is proof of life, not only a beat. A peer sending a day is
       plainly here, so presence never contradicts what just arrived. */
    out[dev] = now;
    return out;
  }

  /** Who is here, at this instant. Sorted, so a list rendered from it does not
      reshuffle itself between two renders for no reason at all. */
  function mdLivePeersAt(table: any, now: number, gone = MD_LIVE_BEAT * MD_LIVE_MISSES): string[] {
    const out: string[] = [];
    for (const [dev, at] of Object.entries(table || {})) {
      if (Number.isFinite(at as number) && now - (at as number) <= gone) out.push(dev);
    }
    return out.sort();
  }

  /* The table, and the last answer the app was given. Compared rather than
     assumed: a beat from a device already listed changes the table and changes
     nothing anybody can see, and re-rendering Settings four times a minute for
     that is a cost with no fact behind it. */
  let peers: Record<string, number> = {};
  let peerDesks: Record<string, string | null> = {};
  let told = '';
  let beating: any = null;

  /** Who is here right now. */
  function mdLivePeers(): string[] {
    return session ? mdLivePeersAt(peers, Date.now()) : [];
  }

  function mdLivePresence(): any[] {
    return mdLivePeers().map((deviceId) => ({
      deviceId,
      activeDesk: Object.prototype.hasOwnProperty.call(peerDesks, deviceId) ? peerDesks[deviceId] : null,
      lastSeen: peers[deviceId],
    }));
  }

  /** Desk changes do not wait for the account heartbeat. This carries only an
      id, never scene content, and remains backward-compatible with peers that
      know presence only as awake/asleep. */
  function mdLiveSetActiveDesk(desk: any): boolean {
    const next = desk == null ? null : String(desk);
    if (next !== null && !/^[a-z0-9]{10}$/.test(next)) return false;
    if (activeDesk === next) return true;
    activeDesk = next;
    if (session) void mdLivePublish({ t: 'beat' });
    return true;
  }

  function announce(): void {
    const now = mdLivePeers();
    const presence = mdLivePresence();
    const key = presence.map((peer) => `${peer.deviceId}:${peer.activeDesk || ''}`).join(',');
    if (key === told) return;
    told = key;
    if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
      g.dispatchEvent(new g.CustomEvent('mdlivepeers', { detail: { peers: now, presence } }));
    }
  }

  /** Say that this device is still here, and let go of anybody who has not.
      One timer doing both jobs: the sweep is what makes a device that closed
      its laptop lid leave the list without a frame arriving to say so. */
  function tick(): void {
    if (!session) return;
    mdLivePublish({ t: 'beat' });
    announce();
  }

  /** Hear what peers say. Returns the way to stop hearing it. */
  function mdLiveOn(fn: (frame: any) => void): () => void {
    if (typeof fn !== 'function') return () => {};
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }

  Object.assign(g, {
    MD_LIVE_COALESCE, MD_LIVE_COALESCE_BOARD, MD_LIVE_BEAT, MD_LIVE_MISSES,
    mdLiveCoalesceFor,
    mdLiveStart, mdLiveStop, mdLiveIsLive, mdLivePublish, mdLiveOn, mdLiveFlush, mdLiveOffer,
    mdLivePeersStep, mdLivePeersAt, mdLivePeers, mdLivePresence, mdLiveSetActiveDesk,
  });
})(typeof window !== 'undefined' ? window : globalThis);
